import { useEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { outcomeNumber } from "@app/components/account-queue/job-card";
import { Link } from "@tanstack/react-router";

import { useToast } from "@app/components/toast";

import type { accountsPage_Query } from "@app/queries/accountsPage_Query.graphql";
import type { accountsPage_MergeMutation } from "@app/queries/accountsPage_MergeMutation.graphql";

// U14 — account mapping: every QBO Bank/CC feed with the canonical account it
// maps to. A second-source feed merges onto an existing statements-fed account
// (auto-suggested by last4 / its own matching records / name, or picked from
// the searchable list); merged feeds unmerge back to their own placeholder.
//
// A merge is a BACKGROUND RUN: the click enqueues it and the row narrates
// (recording → matching → rebuilding the ledger), then shows what it produced.
// It used to run inside the request, and the first merge on the live account
// died with that request — stamped, never re-materialized. Nothing moves in
// the numbers at merge time (records become visible, not different), and a
// merge is reversible, so no confirmation gate stands in front of it.

const pageQuery = graphql`
  query accountsPage_Query {
    sourcingAccounts {
      qboId
      name
      acctType
      merged
      keptSeparate
      bankAccount {
        id
        nickname
        number
      }
      suggestion {
        id
        nickname
        number
      }
      suggestionReasons
      nearTwinCount
      run {
        jobId
        kind
        params
        status
        phase
        currentObject
        rowsProcessed
        rowsTotal
        unit
        errorMessage
        finishedAt
        outcome {
          key
          value
        }
      }
    }
    bankAccounts {
      id
      nickname
      number
      last4
    }
  }
`;

const mergeMutation = graphql`
  mutation accountsPage_MergeMutation(
    $action: String!
    $qboAccountQboId: String!
    $bankAccountId: ID
  ) {
    sourcingAccountMerge(
      action: $action
      qboAccountQboId: $qboAccountQboId
      bankAccountId: $bankAccountId
    ) {
      ok
      preview {
        matched
        claimed
        flagged
        demoted
        pending
      }
      job {
        jobId
        status
      }
      errors {
        message
      }
    }
  }
`;

type PageData = accountsPage_Query["response"];
type QboAccountRow = PageData["sourcingAccounts"][number];
type BankAccountOption = PageData["bankAccounts"][number];
type AccountRun = NonNullable<QboAccountRow["run"]>;

const REASON_LABELS: Record<string, string> = {
  LAST4: "last 4 digits",
  NEAR_TWINS: "matching records",
  NAME: "name",
};

const QBO_PLACEHOLDER_PREFIX = "QBO-";
const POLL_MS = 2000;

const PHASE_LABELS: Record<string, string> = {
  QUEUED: "queued",
  WAITING: "waiting its turn",
  RECORDING: "recording",
  STAGING: "matching QuickBooks records",
  DERIVING: "rebuilding the ledger",
  ATTRIBUTING: "applying rules",
  CATEGORIZING: "categorizing",
};

const isLive = (run: AccountRun | null | undefined) => run?.status === "running";

// One line on the account row: what the run is doing, or what it produced.
function RunStatus({ run, onRetry, busy }: { run: AccountRun; onRetry: () => void; busy: boolean }) {
  const verb = run.kind === "UNMERGE" ? "Unmerging" : "Merging";
  if (run.status === "running") {
    return (
      <p className="text-xs text-indigo-700">
        <span className="font-medium">{verb}…</span>{" "}
        {run.currentObject ?? PHASE_LABELS[run.phase ?? ""] ?? run.phase ?? "queued"}
        {run.rowsTotal ? (
          <span className="tabular-nums text-indigo-500">
            {" "}
            · {run.rowsProcessed.toLocaleString()} / {run.rowsTotal.toLocaleString()} {run.unit ?? ""}
          </span>
        ) : null}
      </p>
    );
  }
  if (run.status === "error") {
    return (
      <p className="flex items-center gap-2 text-xs text-red-700">
        <span>
          <span className="font-medium">{verb} failed:</span> {run.errorMessage}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={onRetry}
          className="rounded border border-red-300 px-2 py-0.5 text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          Retry
        </button>
      </p>
    );
  }
  if ((run.params as Record<string, unknown> | null)?.kind === "MERGE" && run.outcome.length > 0) {
    const o = {
      matched: outcomeNumber(run.outcome, "matched"),
      claimed: outcomeNumber(run.outcome, "claimed"),
      demoted: outcomeNumber(run.outcome, "demoted"),
      flagged: outcomeNumber(run.outcome, "flagged"),
    };
    return (
      <p className="text-xs text-gray-500">
        Merged: {o.matched ?? 0} record{o.matched === 1 ? "" : "s"} matched to bank transactions
        {(o.claimed ?? 0) > 0 && <> ({o.claimed} QuickBooks-backed entr{o.claimed === 1 ? "y" : "ies"} replaced)</>}
        {(o.demoted ?? 0) > 0 && (
          <>
            , {o.demoted} already-counted record{o.demoted === 1 ? "" : "s"} now in the{" "}
            <Link to="/sources/review" className="text-indigo-600 underline">
              review queue
            </Link>
          </>
        )}
        {(o.flagged ?? 0) > 0 && <>, {o.flagged} flagged for review</>}.
      </p>
    );
  }
  return null;
}

export default function SourcesAccountsPage() {
  const notify = useToast();
  const [refreshKey, setRefreshKey] = useState(0);
  const data = useLazyLoadQuery<accountsPage_Query>(
    pageQuery,
    {},
    { fetchKey: refreshKey, fetchPolicy: "store-and-network" },
  );
  const [commit, inFlight] = useMutation<accountsPage_MergeMutation>(mergeMutation);

  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // While any row's run is live the page re-reads every couple of seconds, so
  // the row narrates the run and settles into its outcome without a reload.
  const anyLive = data.sourcingAccounts.some((row) => isLive(row.run));
  useEffect(() => {
    if (!anyLive) return;
    const timer = setInterval(() => setRefreshKey((key) => key + 1), POLL_MS);
    return () => clearInterval(timer);
  }, [anyLive]);

  // The manual picker draws from real (statements-fed) accounts only — a QBO
  // placeholder is a feed's own standalone account, never a merge target.
  const pickable = useMemo(
    () => data.bankAccounts.filter((account) => !account.number.startsWith(QBO_PLACEHOLDER_PREFIX)),
    [data.bankAccounts],
  );

  const refresh = () => {
    setPickerFor(null);
    setSearch("");
    setRefreshKey((key) => key + 1);
  };

  const run = (variables: accountsPage_MergeMutation["variables"], onOk: () => void) => {
    commit({
      variables,
      onCompleted: (response) => {
        const payload = response.sourcingAccountMerge;
        if (payload?.ok) {
          onOk();
        } else {
          notify({
            message: payload?.errors[0]?.message ?? "The action failed.",
            kind: "error",
          });
        }
      },
      onError: () => notify({ message: "The action failed — please try again.", kind: "error" }),
    });
  };

  // Enqueues the merge run; the row picks it up on the next poll.
  const startMerge = (row: QboAccountRow, target: { id: string; nickname: string }) =>
    run({ action: "MERGE", qboAccountQboId: row.qboId, bankAccountId: target.id }, () => {
      notify({ message: `Merging ${row.name} onto ${target.nickname} in the background…`, kind: "info" });
      refresh();
    });

  const unmerge = (row: QboAccountRow) =>
    run({ action: "UNMERGE", qboAccountQboId: row.qboId }, () => {
      notify({ message: `Unmerging ${row.name} in the background…`, kind: "info" });
      refresh();
    });

  // A failed run's Retry is the same action again: the run is idempotent, and a
  // merge whose decision already stands skips straight to the pipeline.
  const retry = (row: QboAccountRow) => {
    if (row.run?.kind === "UNMERGE") return unmerge(row);
    if (row.bankAccount) return startMerge(row, row.bankAccount);
    notify({ message: "Pick the account to merge onto, then merge again.", kind: "error" });
  };

  // The other answer to "what is this QBO account?": deliberately its own
  // thing. Recording only — it silences the near-twin data-health check and
  // merge suggestions; a later merge supersedes it.
  const keepSeparate = (row: QboAccountRow, set: boolean) =>
    run({ action: set ? "KEEP_SEPARATE" : "UNKEEP_SEPARATE", qboAccountQboId: row.qboId }, () => {
      notify({
        message: set ? `${row.name} is kept separate — no more suggestions for it.` : `${row.name} is undecided again.`,
        kind: "success",
      });
      setPickerFor(null);
      refresh();
    });

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Account mapping</h1>
        <p className="text-sm text-gray-500">
          Where each QuickBooks bank / credit-card feed lands. Merge a feed onto the matching
          statements-fed account so both sources describe one timeline.{" "}
          <Link to="/sources" className="text-indigo-600 underline">
            Back to sources
          </Link>
        </p>
      </header>

      <section className="rounded border bg-white">
        {data.sourcingAccounts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">
            No QuickBooks bank or credit-card accounts synced yet.
          </p>
        ) : (
          <ul className="divide-y">
            {data.sourcingAccounts.map((row) => (
              <li key={row.qboId} className="px-4 py-3 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {row.name}
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        {row.acctType ?? "Bank"} · QBO #{row.qboId}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {row.merged && row.bankAccount ? (
                        <>
                          merged into <span className="font-medium">{row.bankAccount.nickname}</span>
                        </>
                      ) : row.keptSeparate ? (
                        <>
                          kept separate — deliberately its own account
                          {row.bankAccount && (
                            <>
                              {" "}
                              <span className="font-medium">{row.bankAccount.nickname}</span>
                            </>
                          )}
                        </>
                      ) : row.bankAccount ? (
                        <>
                          its own account <span className="font-medium">{row.bankAccount.nickname}</span>
                        </>
                      ) : (
                        "not mapped yet — maps on the next sync"
                      )}
                    </p>
                    {row.run && <RunStatus run={row.run} busy={inFlight} onRetry={() => retry(row)} />}
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {isLive(row.run) ? (
                      <span className="text-xs text-indigo-500">working…</span>
                    ) : row.merged ? (
                      <button
                        type="button"
                        disabled={inFlight}
                        onClick={() => unmerge(row)}
                        className="rounded border border-red-200 px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Unmerge
                      </button>
                    ) : row.keptSeparate ? (
                      <button
                        type="button"
                        disabled={inFlight}
                        onClick={() => keepSeparate(row, false)}
                        className="rounded border px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Undo keep separate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setSearch("");
                          setPickerFor(pickerFor === row.qboId ? null : row.qboId);
                        }}
                        className="rounded border px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        {pickerFor === row.qboId ? "Close" : "Merge onto…"}
                      </button>
                    )}
                  </div>
                </div>

                {!row.merged && !isLive(row.run) && row.suggestion && (
                  <div className="flex items-center gap-2 rounded bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900">
                    <span>
                      Looks like <span className="font-medium">{row.suggestion.nickname}</span>
                      {row.nearTwinCount > 0 && (
                        <>
                          {" "}
                          — <span className="tabular-nums font-medium">{row.nearTwinCount.toLocaleString()}</span> of its
                          QuickBooks records match that account's transactions
                        </>
                      )}
                      {row.suggestionReasons.length > 0 && (
                        <span className="text-indigo-500">
                          {" "}
                          (matched on {row.suggestionReasons.map((reason) => REASON_LABELS[reason] ?? reason).join(" + ")})
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      disabled={inFlight}
                      onClick={() => startMerge(row, row.suggestion!)}
                      className="ml-auto rounded border border-indigo-300 px-2 py-1 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                    >
                      Merge
                    </button>
                  </div>
                )}

                {pickerFor === row.qboId && !row.merged && (() => {
                  const matches = matching(pickable, search);
                  return (
                  <div className="rounded border bg-gray-50 p-3 space-y-2">
                    <input
                      type="text"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search accounts…"
                      className="block w-64 rounded border px-2 py-1 text-sm text-gray-800"
                    />
                    <ul className="max-h-48 space-y-1 overflow-y-auto">
                      {matches.map((candidate) => (
                        <li key={candidate.id} className="flex items-center gap-3 text-xs text-gray-700">
                          <span className="truncate">{candidate.nickname}</span>
                          {candidate.last4 && <span className="text-gray-400">····{candidate.last4}</span>}
                          <button
                            type="button"
                            disabled={inFlight}
                            onClick={() => startMerge(row, candidate)}
                            className="ml-auto rounded border border-indigo-300 px-2 py-0.5 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                          >
                            Merge
                          </button>
                        </li>
                      ))}
                      {matches.length === 0 && (
                        <li className="text-xs text-gray-400">No matching accounts.</li>
                      )}
                    </ul>
                    <p className="flex items-center gap-2 text-xs text-gray-400">
                      <span>
                        Not the same account as any of these? Keep it separate — the feed stays its own
                        standalone account, and data-health stops asking.
                      </span>
                      <button
                        type="button"
                        disabled={inFlight}
                        onClick={() => keepSeparate(row, true)}
                        className="ml-auto shrink-0 rounded border px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Keep separate
                      </button>
                    </p>
                  </div>
                  );
                })()}
              </li>
            ))}
          </ul>
        )}
      </section>
      <p className="text-xs text-gray-400">
        Merging is reversible: unmerge restores the feed's own account, re-partitions the matches, and
        keeps every manual bind you made.
      </p>
    </div>
  );
}

function matching(accounts: readonly BankAccountOption[], search: string) {
  const needle = search.trim().toLowerCase();
  if (needle === "") return accounts;
  return accounts.filter(
    (account) =>
      account.nickname.toLowerCase().includes(needle) ||
      account.number.toLowerCase().includes(needle) ||
      (account.last4 ?? "").includes(needle),
  );
}
